import React, { useState, useEffect } from 'react';
import { instanceAPI } from '../services/api';
import Pagination from '../components/Pagination';
import InstanceDialog from '../components/dialogs/InstanceDialog';
import './Instances.css';

interface Instance {
  _id: string;
  name: string;
  mongo_url: string;
  milvus_url: string;
  active: boolean;
  is_default: boolean;
  created_at: string;
}

function Instances() {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', mongo_url: '', milvus_url: '', is_default: false });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDialog, setShowDialog] = useState(false);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  useEffect(() => {
    loadInstances();
  }, []);

  const loadInstances = async () => {
    setLoading(true);
    try {
      const response = await instanceAPI.list();
      setInstances(response.data);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
      showNotification('Erro ao carregar instâncias', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredInstances = instances.filter(instance => 
    instance.name.toLowerCase().includes(searchName.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredInstances.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInstances = filteredInstances.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await instanceAPI.update(editingId, formData);
        showNotification('Instância atualizada com sucesso!', 'success');
      } else {
        await instanceAPI.create({ ...formData, active: true });
        showNotification('Instância criada com sucesso!', 'success');
      }
      
      closeDialog();
      loadInstances();
    } catch (error: any) {
      console.error('Erro ao salvar instância:', error);
      const msg = error?.response?.data?.detail || 'Erro ao salvar instância';
      showNotification(msg, 'error');
    }
  };

  const handleEdit = (instance: Instance) => {
    setFormData({
      name: instance.name,
      mongo_url: instance.mongo_url,
      milvus_url: instance.milvus_url,
      is_default: instance.is_default
    });
    setEditingId(instance._id);
    setShowDialog(true);
  };

  const handleNewInstance = () => {
    setFormData({ name: '', mongo_url: '', milvus_url: '', is_default: false });
    setEditingId(null);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setFormData({ name: '', mongo_url: '', milvus_url: '', is_default: false });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar esta instância?')) {
      try {
        await instanceAPI.delete(id);
        showNotification('Instância deletada com sucesso!', 'success');
        loadInstances();
      } catch (error) {
        console.error('Erro ao deletar:', error);
        showNotification('Erro ao deletar instância', 'error');
      }
    }
  };

  return (
    <div className="instances-container">
      <div className="page-header">
        <h1>Instâncias</h1>

        <button 
          className="btn-add"
          onClick={handleNewInstance}
          title="Adicionar nova instância"
        >
          <span className="btn-icon">+</span>
          <span>Adicionar</span>
        </button>
      </div>

      {/* Dialog para criar/editar instância */}
      <InstanceDialog
        show={showDialog}
        editingId={editingId}
        formData={formData}
        onSubmit={handleSubmit}
        onChange={(data) => setFormData({ ...formData, ...data })}
        onClose={closeDialog}
      />

      {/* Barra de busca */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="🔍 Buscar por nome..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="search-input"
          />
          {searchName && (
            <button 
              className="clear-search"
              onClick={() => setSearchName('')}
              title="Limpar busca"
            >
              ✕
            </button>
          )}
        </div>
        <span className="search-count">
          {filteredInstances.length} instância{filteredInstances.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Cards de instâncias */}
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          {paginatedInstances.length > 0 ? (
            <div className="instances-grid">
              {paginatedInstances.map((instance) => (
                <div key={instance._id} className="instance-card">
                  <div className="card-header">
                    <div className="card-title">
                      <span className="card-icon">🖥️</span>
                      <h3>{instance.name}</h3>
                      {instance.is_default && <span className="default-badge">Padrão</span>}
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-action btn-edit"
                        onClick={() => handleEdit(instance)}
                        title="Editar instância"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-action btn-delete"
                        onClick={() => handleDelete(instance._id)}
                        title="Deletar instância"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-field">
                      <label>MongoDB:</label>
                      <span className="field-value" title={instance.mongo_url}>
                        {instance.mongo_url}
                      </span>
                    </div>
                    <div className="card-field">
                      <label>Milvus:</label>
                      <span className="field-value" title={instance.milvus_url}>
                        {instance.milvus_url}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              Nenhuma instância encontrada{searchName && ` com o nome "${searchName}"`}
            </div>
          )}

          {/* Paginação */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Notificação */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.type === 'success' ? '✅' : '❌'}</span>
          {notification.message}
        </div>
      )}
    </div>
  );
}

export default Instances;

